// src/components/ScopeEnrollmentApproval.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faArrowLeft, faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import SJDEFILogo from '../../images/SJDEFILogo.png';
import '../../css/JuanScope/ScopeRegistration1.css';
import '../../css/JuanScope/ScopeEnrollmentApproval.css'; // Import new CSS file
import useEnrollmentStatus from '../../hooks/userEnrollmentStatus';
import SideNavigation from './SideNavigation';
import SessionManager from '../JuanScope/SessionManager';

function ScopeEnrollmentApproval() {
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);

  const {
    userData,
    applicationStatus,
    loading,
    error,
    handleLogout
  } = useEnrollmentStatus();

  // Set content loaded once data is received
  useEffect(() => {
    if (!loading && (userData.email || error)) {
      // Short delay to ensure smooth transition
      const timer = setTimeout(() => {
        setContentLoaded(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [loading, userData, error]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  const handleBack = () => navigate('/scope-voucher-application');

  const handleNext = () => {
    if (applicationStatus.enrollmentApprovalStatus === 'Complete') {
      navigate('/scope-student-assesment');
    }
  };

  const resolveStatusStyle = (status, userData) => {
    const firstName = userData.firstName || 'Juan';

    // Handle both enrollmentApprovalStatus and enrollmentApprovalAdminStatus
    const effectiveStatus = status.enrollmentApprovalStatus === 'Complete' ? 'Approved' : status.enrollmentApprovalAdminStatus;

    switch (effectiveStatus) {
      case 'Pending':
        return {
          backgroundColor: '#686868',
          textColor: '#FFFFFF',
          statusColor: '#686868',
          message: `Your Enrollment Application is Under Review.\n\nThank you for submitting your enrollment application, ${firstName}. Your application is currently under review, and we are carefully processing the required documents.\n\nPlease be patient as we complete this process. Once your enrollment has been reviewed and approved, you will be notified with further instructions. In the meantime, feel free to check the status regularly and reach out if you have any questions or need assistance. We're here to support you!`,
        };
      case 'Approved':
        return {
          backgroundColor: '#34A853',
          textColor: '#FFFFFF',
          statusColor: '#34A853',
          message: `Enrollment Application Approved!\n\nCongratulations, ${firstName}! Your enrollment application has been approved for AY 2025 - 2026, Term 1. You are now one step closer to becoming an official member of our academic community.\n\nTo finalize your enrollment, please proceed to Step 12: Student Assessment to view your temporary assessment form, and Step 13 for the online tuition payment. Once both steps are completed, you will gain access to Step 14: Officially Enrolled, where you'll find additional details and confirmation of your enrollment status.`,
        };
      case 'Rejected':
        return {
          backgroundColor: '#880D0C',
          textColor: '#FFFFFF',
          statusColor: '#880D0C',
          message: `Enrollment Application Not Approved.\n\nDear ${firstName}, we regret to inform you that your enrollment application was not approved. Please see the detailed reason below and contact the Registrar's Office for further details or to explore alternative options.\n\nShould you have any questions, feel free to reach out to us.`,
        };
      default:
        return {
          backgroundColor: '#686868',
          textColor: '#FFFFFF',
          statusColor: '#686868',
          message: `Your Enrollment Application is Under Review.\n\nDear ${firstName}, your enrollment status is pending further review. Please check back later for updates.`,
        };
    }
  };

  const statusProps = resolveStatusStyle(applicationStatus, userData);

  // Show loading screen only during initial load
  const isLoading = loading && !contentLoaded;

  return (
    <SessionManager>
      <div className="scope-registration-container">
        <header className="juan-register-header">
          <div className="juan-header-left">
            <img src={SJDEFILogo} alt="SJDEFI Logo" className="juan-logo-register" />
            <div className="juan-header-text">
              <h1>JUAN SCOPE</h1>
            </div>
          </div>
          <div className="hamburger-menu">
            <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle navigation menu">
              <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} size="lg" />
            </button>
          </div>
        </header>

        <div className="scope-registration-content">
          <SideNavigation
            userData={userData}
            onNavigate={closeSidebar}
            isOpen={sidebarOpen}
            {...applicationStatus}
          />

          <main className={`scope-main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
            {isLoading ? (
              <div className="scope-loading">
                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
              </div>
            ) : error ? (
              <div className="scope-error">{error}</div>
            ) : (
              <div className="registration-content">
                <h2 className="registration-title">Enrollment Approval</h2>
                <div className="registration-divider"></div>
                <div className="registration-container">
                  <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '1.5rem' }}>
                    Once you have successfully submitted all the required enrollment documents and met the necessary criteria, your enrollment application will be reviewed for final approval.
                  </div>
                  <div className="personal-info-section">
                    <div className="personal-info-header" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#212121' }} />
                      <h3>Enrollment Status:</h3>
                      <span style={{ color: statusProps.statusColor, fontWeight: 'bold', fontSize: '16px' }}>
                        {applicationStatus.enrollmentApprovalStatus === 'Complete' ? 'Approved' : applicationStatus.enrollmentApprovalAdminStatus}
                      </span>
                    </div>
                    <div className="personal-info-divider"></div>
                    <div
                      className="enrollment-approval-reminder-box"
                      style={{
                        backgroundColor: statusProps.backgroundColor,
                        color: statusProps.textColor,
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '1rem',
                      }}
                    >
                      <p style={{ whiteSpace: 'pre-line' }}>{statusProps.message}</p>
                    </div>
                    {applicationStatus.enrollmentApprovalAdminStatus === 'Rejected' && (
                      <div className="rejection-message-box" style={{ marginBottom: '1rem' }}>
                        <p>
                          <strong>Rejection Reason:</strong>{' '}
                          {userData.enrollmentApprovalRejectMessage || 'No specific reason provided.'}
                        </p>
                      </div>
                    )}
                    <div className="form-buttons" style={{ marginTop: '1.5rem' }}>
                      <button type="button" className="back-button" onClick={handleBack}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back
                      </button>
                      <button
                        type="button"
                        className="next-button"
                        onClick={handleNext}
                        disabled={applicationStatus.enrollmentApprovalStatus !== 'Complete'}
                        style={{
                          backgroundColor: applicationStatus.enrollmentApprovalStatus === 'Complete' ? '#34A853' : '#d3d3d3',
                          cursor: applicationStatus.enrollmentApprovalStatus === 'Complete' ? 'pointer' : 'not-allowed',
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>

        {sidebarOpen && <div className="sidebar-overlay active" onClick={toggleSidebar}></div>}

        {showLogoutModal && (
          <div className="scope-modal-overlay">
            <div className="scope-confirm-modal">
              <h3>Confirm Logout</h3>
              <p>Are you sure you want to logout?</p>
              <div className="scope-modal-buttons">
                <button className="scope-modal-cancel" onClick={() => setShowLogoutModal(false)}>
                  Cancel
                </button>
                <button className="scope-modal-confirm" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SessionManager>
  );
}

export default ScopeEnrollmentApproval;